// screens/staff/BozzeQueueScreen.tsx
//
// W7 Session 2 — corpus draft review surface per spec §7 + ROADMAP §W7
// task 4. Visible to titolare + instructor_teoria + instructor_guida
// (segreteria gets 403 from the BE + the side-nav hides "Bozze"). Italian
// "Lei" voice on every staff-facing string.
//
// Layout:
//   - List view: pending drafts grouped by created_at desc, citation-prefixed
//     row + filename + relative timestamp + first-100-chars excerpt.
//   - Detail view (modal overlay): side-by-side diff (current principle text
//     vs current+proposed_additions, with additions highlighted in tokens.rightSoft);
//     3 action buttons: Approva / Modifica e approva / Rifiuta.
//   - Confirmation modals for Approva (verbatim spec copy) + Rifiuta (notes
//     textarea); Modifica e approva is an inline editor that toggles below
//     the diff and POSTs the edited text on Salva e approva.
//
// Modal a11y (do NOT repeat the W6 v1 Frequenze regression):
//   - Each modal: aria-modal="true", aria-labelledby="<h2-id>",
//     aria-describedby="<body-id>" when useful, default focus on the
//     primary CTA, Esc dismisses, click-outside dismisses.
//
// Diff highlight: tokens.rightSoft as the addition background + a leading
// "+" prefix as the non-color cue (WCAG 1.4.1 — never communicate
// state with color alone). Side-by-side columns split 50/50 on desktop.

import { useEffect, useRef, useState, type JSX } from 'react';
import { useTokens } from '../../state/theme';
import {
  staffApi,
  type DraftListItem,
  type DraftDetailPayload,
  type ArgomentoPickerRow,
  type PrinciplePickerRow,
} from '../../api/staff';
import { StaffApiError } from '../../api/staff-client';

/** W7 Session 3 — upload widget validation: reuse the BE constants. */
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;  // 10 MB — matches CORPUS_MAX_UPLOAD_BYTES default

type ModalKind = 'approve' | 'reject' | null;

// ---------------------------------------------------------------------------
// Modal stack — topmost-only Esc handler
// ---------------------------------------------------------------------------
//
// Each modal mount registers a `window.addEventListener('keydown', ...)` so
// Esc can dismiss it. When two modals stack (DetailModal + ApproveConfirm /
// DetailModal + Reject), one Esc fires both listeners and BOTH modals close
// — which yanks the user out of the entire flow when they only meant to
// cancel the inner confirmation.
//
// Fix: a module-level stack of unique Symbols. Each modal pushes on mount
// and pops on unmount; the keydown handler bails out unless its symbol is
// at the top of the stack. Strict-mode double-invoke is fine — the stack
// is push/pop balanced.
const modalStack: symbol[] = [];

function pushModal(): symbol {
  const s = Symbol('bozze-modal');
  modalStack.push(s);
  return s;
}

function popModal(s: symbol): void {
  const idx = modalStack.lastIndexOf(s);
  if (idx >= 0) modalStack.splice(idx, 1);
}

function isTopmostModal(s: symbol): boolean {
  return modalStack[modalStack.length - 1] === s;
}

interface OpenModal {
  kind: ModalKind;
  /** Optional copy of the draft id so the modal can submit even if the
   *  detail panel state churns mid-render. */
  draftId: string | null;
}

export default function BozzeQueueScreen(): JSX.Element {
  const tokens = useTokens();
  const [drafts, setDrafts] = useState<DraftListItem[] | null>(null);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionInfo, setActionInfo] = useState<string | null>(null);

  // Detail surface state — when set, we render the right-side detail panel
  // covering the list with a backdrop. Spec §5.4 LacuneQueueScreen uses an
  // expand-in-place pattern; here we go modal-style because the diff view
  // wants more horizontal space than a row gives.
  const [detailDraftId, setDetailDraftId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DraftDetailPayload | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState<boolean>(false);
  const [editorText, setEditorText] = useState<string>('');

  const [modal, setModal] = useState<OpenModal>({ kind: null, draftId: null });
  const [approvalBusy, setApprovalBusy] = useState(false);
  const approvalBusyRef = useRef(false);
  const [approvalError, setApprovalError] = useState<{ message: string; blocked: boolean } | null>(null);

  function fetchList() {
    setLoading(true);
    setError(null);
    return staffApi
      .listDrafts({ status: 'pending', limit: 50, offset: 0 })
      .then((res) => {
        setDrafts(res.drafts);
        setPendingCount(res.pending_count);
        setLoading(false);
      })
      .catch(() => {
        setError('Non riesco a caricare le bozze. Riprovi.');
        setLoading(false);
      });
  }

  useEffect(() => {
    let alive = true;
    // Initial-fetch effect — setting loading/error here is a one-time
    // mount sync, not a render-loop hazard. Same pattern as the other
    // staff screens (LacuneQueueScreen, MetricheScreen, ClassPulseScreen).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    staffApi
      .listDrafts({ status: 'pending', limit: 50, offset: 0 })
      .then((res) => {
        if (!alive) return;
        setDrafts(res.drafts);
        setPendingCount(res.pending_count);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setError('Non riesco a caricare le bozze. Riprovi.');
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Load draft detail when a row is opened.
  useEffect(() => {
    if (!detailDraftId) {
      // Reset detail-pane state when the modal closes. This is the legit
      // sync direction (URL/route → local state) that React's lint rule
      // flags conservatively; same pattern used elsewhere in /staff.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDetail(null);
      setDetailError(null);
      setEditorOpen(false);
      setEditorText('');
      return;
    }
    let alive = true;
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    setEditorOpen(false);
    setEditorText('');
    staffApi
      .getDraft(detailDraftId)
      .then((res) => {
        if (!alive) return;
        setDetail(res);
        setEditorText(res.draft.proposed_additions ?? '');
        setDetailLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        if (err instanceof StaffApiError && err.status === 404) {
          setDetailError('Bozza non trovata.');
        } else {
          setDetailError('Non riesco a caricare la bozza. Riprovi.');
        }
        setDetailLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [detailDraftId]);

  function closeDetail() {
    if (approvalBusyRef.current) return;
    setDetailDraftId(null);
  }

  function openModal(kind: Exclude<ModalKind, null>, draftId: string) {
    setActionError(null);
    setActionInfo(null);
    setApprovalError(null);
    setModal({ kind, draftId });
  }

  function closeModal() {
    if (approvalBusyRef.current) return;
    setModal({ kind: null, draftId: null });
  }

  async function handleApprove(opts: { editedText?: string }) {
    const draftId = modal.draftId;
    if (!draftId || approvalBusyRef.current) return;
    approvalBusyRef.current = true;
    setApprovalBusy(true);
    setApprovalError(null);
    try {
      await staffApi.approveDraft(draftId, {
        edited_proposed_additions: opts.editedText,
      });
      setActionInfo('Bozza approvata.');
      setModal({ kind: null, draftId: null });
      setDetailDraftId(null);
      await fetchList();
      // Tell StaffDashboardLayout to refresh the sidenav "Bozze" badge.
      // The pending count just dropped by 1 — without this dispatch, the
      // badge would stay stale until the next full-page reload.
      window.dispatchEvent(new CustomEvent('bozze:pending-count-changed'));
    } catch (err) {
      if (err instanceof StaffApiError && err.status === 409
        && (err.body as { error?: string } | null)?.error === 'publication_requires_identity_migration') {
        setApprovalError({ blocked: true, message:
          'La pubblicazione è temporaneamente non disponibile. La proposta originale è salvata tra le bozze.'
          + (opts.editedText !== undefined ? ' Le modifiche inserite restano in questa schermata e non sono state salvate.' : ''),
        });
      } else if (err instanceof StaffApiError && err.status === 409) {
        setActionError(
          'Un altro istruttore ha già revisionato questa bozza. Aggiorno la lista.',
        );
        setModal({ kind: null, draftId: null });
        setDetailDraftId(null);
        await fetchList();
        // 409 also implies someone else approved/rejected → count changed.
        window.dispatchEvent(new CustomEvent('bozze:pending-count-changed'));
      } else {
        setApprovalError({ blocked: false, message:
          'Approvazione non riuscita. La bozza e le modifiche in questa schermata restano disponibili. Riprovi.',
        });
      }
    } finally {
      approvalBusyRef.current = false;
      setApprovalBusy(false);
    }
  }

  async function handleReject(notes: string) {
    const draftId = modal.draftId;
    closeModal();
    if (!draftId) return;
    try {
      await staffApi.rejectDraft(draftId, {
        review_notes: notes.trim() || undefined,
      });
      setActionInfo('Bozza rifiutata.');
      closeDetail();
      await fetchList();
      window.dispatchEvent(new CustomEvent('bozze:pending-count-changed'));
    } catch (err) {
      if (err instanceof StaffApiError && err.status === 409) {
        setActionError(
          'Un altro istruttore ha già revisionato questa bozza. Aggiorno la lista.',
        );
        closeDetail();
        await fetchList();
        window.dispatchEvent(new CustomEvent('bozze:pending-count-changed'));
      } else {
        setActionError('Rifiuto non riuscito. Riprovi.');
      }
    }
  }

  return (
    <section data-testid="bozze-queue-screen">
      <header style={{ marginBottom: 24 }}>
        <h1
          style={{
            fontFamily: tokens.fonts.serif,
            fontSize: 26,
            fontWeight: 700,
            margin: 0,
          }}
        >
          Bozze da rivedere
        </h1>
        <p
          style={{
            color: tokens.textMuted,
            margin: '4px 0 0 0',
            fontSize: 13,
          }}
        >
          {pendingPretty(pendingCount)}
        </p>
      </header>

      {/* W7 Session 3 — upload widget at the top of the page (above the
          pending-drafts list). Coordinator decision: NOT a separate route. */}
      <UploadWidget
        onDraftCreated={() => {
          // Refresh the list so the new draft appears.
          setActionInfo('Bozza generata. La trovi qui sotto.');
          fetchList();
          window.dispatchEvent(new CustomEvent('bozze:pending-count-changed'));
        }}
      />

      {actionError && (
        <div
          role="alert"
          data-testid="bozze-action-error"
          style={{ marginBottom: 16, color: tokens.wrong, fontSize: 13 }}
        >
          {actionError}
        </div>
      )}
      {actionInfo && (
        <div
          role="status"
          data-testid="bozze-action-info"
          style={{ marginBottom: 16, color: tokens.right, fontSize: 13 }}
        >
          {actionInfo}
        </div>
      )}
      {error && (
        <div
          role="alert"
          data-testid="bozze-error"
          style={{ marginBottom: 16, color: tokens.wrong, fontSize: 13 }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div
          data-testid="bozze-loading"
          style={{ color: tokens.textMuted, fontSize: 13 }}
        >
          Carico le bozze…
        </div>
      ) : drafts && drafts.length === 0 ? (
        <div
          data-testid="bozze-empty"
          style={{
            color: tokens.textMuted,
            fontSize: 14,
            padding: '32px 0',
          }}
        >
          <div>Nessuna bozza in attesa.</div>
          <div style={{ fontSize: 12, marginTop: 6, color: tokens.textFaint }}>
            Le bozze vengono generate dalle ricerche LLM o dalle lacune.
          </div>
        </div>
      ) : (
        drafts && (
          <div
            data-testid="bozze-list"
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            {drafts.map((d) => (
              <DraftRow
                key={d.id}
                draft={d}
                onOpen={() => setDetailDraftId(d.id)}
              />
            ))}
          </div>
        )
      )}

      {/* Detail surface (modal overlay) */}
      {detailDraftId && (
        <DetailModal
          draftId={detailDraftId}
          loading={detailLoading}
          error={detailError}
          detail={detail}
          editorOpen={editorOpen}
          editorText={editorText}
          onEditorTextChange={setEditorText}
          onToggleEditor={() => setEditorOpen((v) => !v)}
          onCancel={closeDetail}
          onApproveOriginal={() => openModal('approve', detailDraftId)}
          onApproveEdited={() => openModal('approve', detailDraftId)}
          onReject={() => openModal('reject', detailDraftId)}
        />
      )}

      {/* Confirmation modals */}
      {modal.kind === 'approve' && modal.draftId && (
        <ApproveConfirmModal
          // editorOpen flag controls whether we send the edited text or
          // the unedited proposal. Capture both safely.
          editedText={editorOpen ? editorText : undefined}
          busy={approvalBusy}
          error={approvalError}
          onCancel={closeModal}
          onConfirm={(t) => handleApprove({ editedText: t })}
        />
      )}
      {modal.kind === 'reject' && modal.draftId && (
        <RejectModal onCancel={closeModal} onConfirm={handleReject} />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function DraftRow({
  draft,
  onOpen,
}: {
  draft: DraftListItem;
  onOpen: () => void;
}): JSX.Element {
  const tokens = useTokens();
  const subtitle = draft.principle?.name_it ?? 'Bozza senza principio collegato';
  const sourceLabel = draft.source_document
    ? draft.source_document.filename
    : draft.created_by_kind === 'gap_queue'
      ? 'Da lacuna'
      : 'Da contenuto generato dal sistema';
  const excerpt = (draft.proposed_additions ?? '').trim();
  const truncated =
    excerpt.length > 100 ? excerpt.slice(0, 100) + '…' : excerpt || '(nessuna proposta)';
  const citation = draft.principle?.citation ?? '';

  return (
    <div
      data-testid={`bozza-row-${draft.id}`}
      style={{
        background: tokens.surface,
        border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radius.lg,
        padding: '16px 18px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              marginBottom: 6,
              flexWrap: 'wrap',
            }}
          >
            {citation && (
              <span style={{ fontSize: 12, color: tokens.accent, fontWeight: 600 }}>
                {citation}
              </span>
            )}
            <span style={{ fontSize: 13, color: tokens.text, fontWeight: 600 }}>
              {subtitle}
            </span>
            <span style={{ fontSize: 12, color: tokens.textMuted }}>
              · {sourceLabel}
            </span>
            <span style={{ fontSize: 12, color: tokens.textMuted }}>
              · {formatRelativeIt(draft.created_at)}
            </span>
          </div>
          <div
            data-testid={`bozza-excerpt-${draft.id}`}
            style={{ fontSize: 13, color: tokens.textMuted, fontStyle: 'italic' }}
          >
            «{truncated}»
          </div>
        </div>
        <button
          data-testid={`bozza-open-${draft.id}`}
          onClick={onOpen}
          style={{
            background: 'transparent',
            color: tokens.text,
            border: `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.md,
            padding: '8px 14px',
            cursor: 'pointer',
            fontSize: 13,
            minHeight: 44,
          }}
        >
          Visualizza dettagli
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail modal
// ---------------------------------------------------------------------------

function DetailModal(props: {
  draftId: string;
  loading: boolean;
  error: string | null;
  detail: DraftDetailPayload | null;
  editorOpen: boolean;
  editorText: string;
  onEditorTextChange: (t: string) => void;
  onToggleEditor: () => void;
  onCancel: () => void;
  onApproveOriginal: () => void;
  onApproveEdited: () => void;
  onReject: () => void;
}): JSX.Element {
  const tokens = useTokens();
  const ref = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<Element | null>(null);
  // Stable ref to onCancel so the mount-only effect can call the freshest
  // handler without re-firing on every parent render. (Without this, the
  // effect deps list `[props]` re-fires every keystroke because the parent
  // creates a fresh `props` object each render.)
  const onCancelRef = useRef(props.onCancel);
  useEffect(() => {
    onCancelRef.current = props.onCancel;
  }, [props.onCancel]);

  // Focus management + topmost-only Esc handler. Mount-only deps so the
  // effect runs once per actual mount — NOT on every parent keystroke.
  useEffect(() => {
    const myStackEntry = pushModal();
    lastFocused.current = document.activeElement;
    const node = ref.current;
    if (node) {
      const initial = node.querySelector<HTMLElement>('[data-modal-initial-focus]') ?? node;
      initial.focus();
    }
    function onKeyDown(e: KeyboardEvent) {
      // Topmost-only Esc: when DetailModal + ApproveConfirmModal are both
      // mounted, only the topmost one (ApproveConfirmModal) handles Esc.
      // Without this guard, a single Esc closes both modals.
      if (e.key === 'Escape') {
        if (!isTopmostModal(myStackEntry)) return;
        e.preventDefault();
        onCancelRef.current();
        return;
      }
      // Tab focus-trap — keep focus inside the modal's focusable elements.
      if (e.key === 'Tab' && node) {
        const focusables = node.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      popModal(myStackEntry);
      if (lastFocused.current instanceof HTMLElement) lastFocused.current.focus();
    };
  }, []);

  return (
    <div
      data-testid="bozze-detail-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bozze-detail-title"
      aria-describedby="bozze-detail-body"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 900,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onCancel();
      }}
    >
      <div
        ref={ref}
        data-testid="bozze-detail"
        tabIndex={-1}
        style={{
          background: tokens.surface,
          color: tokens.text,
          borderRadius: tokens.radius.lg,
          padding: '24px 28px',
          maxWidth: 1100,
          width: '95%',
          maxHeight: '90vh',
          overflowY: 'auto',
          fontFamily: tokens.fonts.body,
        }}
      >
        <h2
          id="bozze-detail-title"
          style={{
            fontFamily: tokens.fonts.serif,
            fontSize: 22,
            fontWeight: 700,
            margin: '0 0 12px 0',
          }}
        >
          {props.detail?.principle?.citation
            ? `Bozza per ${props.detail.principle.citation}`
            : 'Bozza per principio sconosciuto'}
        </h2>
        <div id="bozze-detail-body">
          {props.loading && (
            <div
              data-testid="bozze-detail-loading"
              style={{ color: tokens.textMuted, fontSize: 13 }}
            >
              Carico la bozza…
            </div>
          )}
          {props.error && (
            <div
              role="alert"
              data-testid="bozze-detail-error"
              style={{ color: tokens.wrong, fontSize: 13, marginBottom: 12 }}
            >
              {props.error}
            </div>
          )}
          {props.detail && (
            <DiffPanel
              detail={props.detail}
              editorOpen={props.editorOpen}
              editorText={props.editorText}
              onEditorTextChange={props.onEditorTextChange}
            />
          )}
        </div>

        {/* Action row */}
        {props.detail && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginTop: 16,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            <button
              data-testid="bozze-detail-cancel"
              onClick={props.onCancel}
              style={secondaryButtonStyle(tokens)}
            >
              Chiudi
            </button>
            {props.editorOpen ? (
              <>
                <button
                  data-testid="bozze-detail-cancel-edit"
                  onClick={props.onToggleEditor}
                  style={secondaryButtonStyle(tokens)}
                >
                  Annulla modifica
                </button>
                <button
                  data-testid="bozze-detail-approve-edited"
                  data-modal-initial-focus
                  onClick={props.onApproveEdited}
                  style={primaryButtonStyle(tokens)}
                >
                  Salva e approva
                </button>
              </>
            ) : (
              <>
                <button
                  data-testid="bozze-detail-reject"
                  onClick={props.onReject}
                  style={mutedButtonStyle(tokens)}
                >
                  Rifiuta
                </button>
                <button
                  data-testid="bozze-detail-edit"
                  onClick={props.onToggleEditor}
                  style={secondaryButtonStyle(tokens)}
                >
                  Modifica e approva
                </button>
                <button
                  data-testid="bozze-detail-approve"
                  data-modal-initial-focus
                  onClick={props.onApproveOriginal}
                  style={primaryButtonStyle(tokens)}
                >
                  Approva
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Diff panel (side-by-side)
// ---------------------------------------------------------------------------

function DiffPanel({
  detail,
  editorOpen,
  editorText,
  onEditorTextChange,
}: {
  detail: DraftDetailPayload;
  editorOpen: boolean;
  editorText: string;
  onEditorTextChange: (t: string) => void;
}): JSX.Element {
  const tokens = useTokens();
  const principleText = detail.principle?.enunciato ?? '';
  const additions = detail.draft.proposed_additions ?? '';
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
        }}
      >
        {/* LEFT — current principle text */}
        <div
          data-testid="bozze-diff-left"
          style={{
            background: tokens.bg,
            border: `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.md,
            padding: '12px 14px',
            minHeight: 120,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: tokens.textMuted,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              marginBottom: 6,
            }}
          >
            Principio attuale
          </div>
          <div style={{ fontSize: 13, color: tokens.text, whiteSpace: 'pre-wrap' }}>
            {principleText || '—'}
          </div>
        </div>
        {/* RIGHT — current + proposed additions */}
        <div
          data-testid="bozze-diff-right"
          style={{
            background: tokens.bg,
            border: `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.md,
            padding: '12px 14px',
            minHeight: 120,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: tokens.textMuted,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              marginBottom: 6,
            }}
          >
            Con la proposta del Prof
          </div>
          <div style={{ fontSize: 13, color: tokens.text, whiteSpace: 'pre-wrap' }}>
            {principleText}
          </div>
          {additions.trim() && (
            <div
              data-testid="bozze-diff-additions"
              style={{
                marginTop: 10,
                background: tokens.rightSoft,
                borderLeft: `3px solid ${tokens.right}`,
                padding: '8px 12px',
                borderRadius: tokens.radius.sm,
                fontSize: 13,
                color: tokens.text,
                whiteSpace: 'pre-wrap',
                fontWeight: 500,
              }}
            >
              <span aria-hidden="true" style={{ marginRight: 6, color: tokens.right, fontWeight: 700 }}>
                +
              </span>
              <span data-testid="bozze-diff-additions-text">{additions}</span>
            </div>
          )}
        </div>
      </div>

      {/* Editor (toggle) */}
      {editorOpen && (
        <div style={{ marginTop: 16 }}>
          <label
            htmlFor="bozze-edit-textarea"
            style={{
              fontSize: 12,
              color: tokens.textMuted,
              display: 'block',
              marginBottom: 6,
            }}
          >
            Modifichi la proposta prima di approvare:
          </label>
          <textarea
            id="bozze-edit-textarea"
            data-testid="bozze-edit-textarea"
            value={editorText}
            onChange={(e) => onEditorTextChange(e.target.value)}
            rows={6}
            // BE Zod schema: edited_proposed_additions max 4000 chars.
            // Hard-cap in the FE so the user can't compose past the limit.
            maxLength={4000}
            style={{
              width: '100%',
              background: tokens.bg,
              color: tokens.text,
              border: `1px solid ${tokens.line}`,
              borderRadius: tokens.radius.md,
              padding: '10px 12px',
              fontFamily: tokens.fonts.body,
              fontSize: 14,
              resize: 'vertical',
            }}
          />
          <div
            data-testid="bozze-edit-textarea-counter"
            style={{
              fontSize: 11,
              color: tokens.textMuted,
              textAlign: 'right',
              marginTop: 4,
            }}
          >
            {editorText.length}/4000
          </div>
        </div>
      )}

      {/* Citations */}
      {Array.isArray(detail.draft.citations) && detail.draft.citations.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 11,
              color: tokens.textMuted,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              marginBottom: 6,
            }}
          >
            Citazioni dal documento
          </div>
          <div data-testid="bozze-citations" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(detail.draft.citations as Array<Record<string, unknown>>).map((c, i) => {
              const page = typeof c.page === 'number' ? c.page : null;
              const excerpt = typeof c.excerpt === 'string' ? c.excerpt : '';
              return (
                <div
                  key={i}
                  data-testid={`bozze-citation-${i}`}
                  style={{
                    background: tokens.drawerBg,
                    border: `1px solid ${tokens.line}`,
                    borderRadius: tokens.radius.sm,
                    padding: '8px 10px',
                    fontSize: 12,
                    color: tokens.text,
                  }}
                >
                  {page != null && (
                    <span style={{ color: tokens.textMuted, marginRight: 6 }}>
                      Pagina {page}:
                    </span>
                  )}
                  «{excerpt}»
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Source document link (signed_url, 1h TTL) */}
      {detail.source_document && (
        <div style={{ marginTop: 12, fontSize: 12, color: tokens.textMuted }}>
          Documento di origine: <strong>{detail.source_document.filename}</strong>
          {detail.source_document.signed_url && (
            <>
              {' · '}
              <a
                data-testid="bozze-source-link"
                href={detail.source_document.signed_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: tokens.accent }}
              >
                Apri il PDF
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Approve confirmation modal
// ---------------------------------------------------------------------------

function ApproveConfirmModal({
  editedText,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  editedText: string | undefined;
  busy: boolean;
  error: { message: string; blocked: boolean } | null;
  onCancel: () => void;
  onConfirm: (text?: string) => void;
}): JSX.Element {
  const tokens = useTokens();
  const ref = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<Element | null>(null);
  // Stable refs — mount-only effect uses these to dispatch fresh handlers
  // without re-firing.
  const onCancelRef = useRef(onCancel);
  useEffect(() => {
    onCancelRef.current = onCancel;
  }, [onCancel]);
  useEffect(() => {
    const myStackEntry = pushModal();
    lastFocused.current = document.activeElement;
    const node = ref.current;
    if (node) {
      const initial =
        node.querySelector<HTMLElement>('[data-modal-initial-focus]') ?? node;
      initial.focus();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (!isTopmostModal(myStackEntry)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancelRef.current();
        return;
      }
      if (e.key === 'Tab' && node) {
        const focusables = node.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) {
          e.preventDefault();
          node.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!Array.from(focusables).some(element => element === document.activeElement)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      popModal(myStackEntry);
      if (lastFocused.current instanceof HTMLElement) lastFocused.current.focus();
    };
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (busy) node.focus();
    else node.querySelector<HTMLElement>(error?.blocked
      ? '[data-testid="modal-approve-cancel"]'
      : '[data-modal-initial-focus]')?.focus();
  }, [busy, error?.blocked]);

  return (
    <div
      data-testid="modal-approve-backdrop"
      role="dialog"
      aria-modal="true"
      aria-busy={busy}
      aria-labelledby="modal-approve-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={ref}
        data-testid="modal-approve"
        tabIndex={-1}
        style={{
          background: tokens.surface,
          color: tokens.text,
          borderRadius: tokens.radius.lg,
          padding: '24px 28px',
          maxWidth: 520,
          width: '90%',
          fontFamily: tokens.fonts.body,
        }}
      >
        <h2
          id="modal-approve-title"
          style={{
            fontFamily: tokens.fonts.serif,
            fontSize: 22,
            fontWeight: 700,
            margin: '0 0 12px 0',
          }}
        >
          Approva la bozza
        </h2>
        <p style={{ fontSize: 14, color: tokens.text, margin: '0 0 16px 0' }}>
          L’approvazione può essere confermata solo quando la pubblicazione è disponibile.
          La proposta originale rimane salvata nelle bozze.
        </p>
        {error && <p role="alert" data-testid="bozze-approval-error"
          style={{ fontSize: 14, color: tokens.text, margin: '0 0 16px 0' }}>{error.message}</p>}
        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          <button
            data-testid="modal-approve-cancel"
            disabled={busy}
            onClick={onCancel}
            style={secondaryButtonStyle(tokens)}
          >
            {error?.blocked ? 'Torna alla bozza' : 'Annulla'}
          </button>
          {!error?.blocked && <button
            data-testid="modal-approve-confirm"
            data-modal-initial-focus
            disabled={busy}
            onClick={() => onConfirm(editedText)}
            style={primaryButtonStyle(tokens)}
          >
            {busy ? 'Verifica in corso…' : error ? 'Riprova' : 'Conferma'}
          </button>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reject modal
// ---------------------------------------------------------------------------

function RejectModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: (notes: string) => void;
}): JSX.Element {
  const tokens = useTokens();
  const ref = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<Element | null>(null);
  const [notes, setNotes] = useState<string>('');
  const onCancelRef = useRef(onCancel);
  useEffect(() => {
    onCancelRef.current = onCancel;
  }, [onCancel]);

  useEffect(() => {
    const myStackEntry = pushModal();
    lastFocused.current = document.activeElement;
    const node = ref.current;
    if (node) {
      const initial =
        node.querySelector<HTMLElement>('[data-modal-initial-focus]') ?? node;
      initial.focus();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (!isTopmostModal(myStackEntry)) return;
        e.preventDefault();
        onCancelRef.current();
        return;
      }
      if (e.key === 'Tab' && node) {
        const focusables = node.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      popModal(myStackEntry);
      if (lastFocused.current instanceof HTMLElement) lastFocused.current.focus();
    };
  }, []);

  return (
    <div
      data-testid="modal-reject-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-reject-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={ref}
        data-testid="modal-reject"
        tabIndex={-1}
        style={{
          background: tokens.surface,
          color: tokens.text,
          borderRadius: tokens.radius.lg,
          padding: '24px 28px',
          maxWidth: 520,
          width: '90%',
          fontFamily: tokens.fonts.body,
        }}
      >
        <h2
          id="modal-reject-title"
          style={{
            fontFamily: tokens.fonts.serif,
            fontSize: 22,
            fontWeight: 700,
            margin: '0 0 12px 0',
          }}
        >
          Rifiuta la bozza?
        </h2>
        <label
          htmlFor="modal-reject-textarea"
          style={{
            fontSize: 12,
            color: tokens.textMuted,
            display: 'block',
            marginBottom: 6,
          }}
        >
          Note (facoltativo)
        </label>
        <textarea
          id="modal-reject-textarea"
          data-testid="modal-reject-textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="Motivo del rifiuto (facoltativo)…"
          // BE Zod schema: review_notes max 1000 chars.
          maxLength={1000}
          style={{
            width: '100%',
            background: tokens.bg,
            color: tokens.text,
            border: `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.md,
            padding: '10px 12px',
            fontFamily: tokens.fonts.body,
            fontSize: 14,
            resize: 'vertical',
          }}
        />
        <div
          data-testid="modal-reject-textarea-counter"
          style={{
            fontSize: 11,
            color: tokens.textMuted,
            textAlign: 'right',
            marginTop: 4,
            marginBottom: 16,
          }}
        >
          {notes.length}/1000
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          <button
            data-testid="modal-reject-cancel"
            onClick={onCancel}
            style={secondaryButtonStyle(tokens)}
          >
            Annulla
          </button>
          <button
            data-testid="modal-reject-confirm"
            data-modal-initial-focus
            onClick={() => onConfirm(notes)}
            style={primaryButtonStyle(tokens)}
          >
            Conferma rifiuto
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// W7 Session 3 — UploadWidget: file picker → argomento/principle picker →
// "Genera bozza dal documento" → list refresh
// ---------------------------------------------------------------------------
//
// 3 phases:
//   1. idle — file input + helper text
//   2. uploaded — file uploaded; shows argomento dropdown + principle
//      dropdown (filtered by argomento) + "Genera bozza dal documento" CTA
//   3. researching — research call in flight; CTA disabled
//
// Errors are surfaced in an inline toast above the widget body. On the
// research success path the widget clears its own state + the parent
// refetches the pending-drafts list.

type UploadWidgetPhase = 'idle' | 'uploaded' | 'researching';

function UploadWidget({
  onDraftCreated,
}: {
  onDraftCreated: () => void;
}): JSX.Element {
  const tokens = useTokens();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [phase, setPhase] = useState<UploadWidgetPhase>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [uploadedDoc, setUploadedDoc] = useState<{
    source_document_id: string;
    filename: string;
  } | null>(null);

  // Argomento + principle picker state (loaded after a successful upload).
  const [argomenti, setArgomenti] = useState<ArgomentoPickerRow[] | null>(null);
  const [argomentiError, setArgomentiError] = useState<string | null>(null);
  const [principles, setPrinciples] = useState<PrinciplePickerRow[] | null>(null);
  const [principlesError, setPrinciplesError] = useState<string | null>(null);
  const [selectedArgomentoId, setSelectedArgomentoId] = useState<string>('');
  const [selectedPrincipleId, setSelectedPrincipleId] = useState<string>('');
  const [principlesLoading, setPrinciplesLoading] = useState<boolean>(false);

  function resetWidget() {
    setPhase('idle');
    setErrorMsg(null);
    setUploadedDoc(null);
    setSelectedArgomentoId('');
    setSelectedPrincipleId('');
    setPrinciples(null);
    setPrinciplesError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    setErrorMsg(null);
    // Client-side validation — matches BE constraints.
    if (file.type !== 'application/pdf') {
      setErrorMsg('Il file deve essere un PDF.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size <= 0) {
      setErrorMsg('Il file è vuoto.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setErrorMsg('Il file supera il limite di 10 MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    // Auto-upload on file pick — cleaner than a 2-step "Carica" press.
    setPhase('researching');  // re-uses the in-flight phase to disable the input during upload
    try {
      const res = await staffApi.uploadCorpusDocument(file);
      setUploadedDoc({
        source_document_id: res.source_document_id,
        filename: res.filename,
      });
      // Lazily fetch argomenti only after a successful upload so we don't
      // burden the staff dashboard with an extra request on every page load.
      if (!argomenti) {
        try {
          const a = await staffApi.listCorpusArgomenti();
          setArgomenti(a.argomenti);
        } catch {
          setArgomentiError('Non riesco a caricare gli argomenti. Riprovi.');
        }
      }
      setPhase('uploaded');
    } catch (err) {
      if (err instanceof StaffApiError) {
        const body = err.body as { reason?: string } | null;
        if (err.status === 400 && body?.reason === 'file_too_large') {
          setErrorMsg('Il file supera il limite di 10 MB.');
        } else if (err.status === 400 && body?.reason === 'unsupported_mime_type') {
          setErrorMsg('Il file deve essere un PDF.');
        } else if (err.status === 400) {
          setErrorMsg('Caricamento non riuscito. Verifichi il file e riprovi.');
        } else if (err.status === 401 || err.status === 403) {
          setErrorMsg('Non ha i permessi per caricare documenti.');
        } else {
          setErrorMsg('Errore durante il caricamento. Riprovi.');
        }
      } else {
        setErrorMsg('Errore durante il caricamento. Riprovi.');
      }
      setPhase('idle');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleArgomentoChange(argId: string) {
    setSelectedArgomentoId(argId);
    setSelectedPrincipleId('');
    setPrinciples(null);
    setPrinciplesError(null);
    if (!argId) return;
    setPrinciplesLoading(true);
    try {
      const res = await staffApi.listCorpusPrinciplesForArgomento(argId);
      setPrinciples(res.principles);
    } catch {
      setPrinciplesError('Non riesco a caricare i principi. Riprovi.');
    } finally {
      setPrinciplesLoading(false);
    }
  }

  async function handleGenerate() {
    if (!uploadedDoc || !selectedPrincipleId) return;
    setPhase('researching');
    setErrorMsg(null);
    try {
      await staffApi.researchFromDocument({
        source_document_id: uploadedDoc.source_document_id,
        principle_id: selectedPrincipleId,
      });
      // Clear + delegate refresh to parent.
      resetWidget();
      onDraftCreated();
    } catch (err) {
      if (err instanceof StaffApiError && err.status === 502) {
        setErrorMsg('Il modello non è disponibile. Riprovi tra qualche minuto.');
      } else if (err instanceof StaffApiError && err.status === 404) {
        setErrorMsg('Documento o principio non trovato. Riprovi.');
      } else {
        setErrorMsg('Errore durante la generazione. Riprovi.');
      }
      setPhase('uploaded');
    }
  }

  const headingId = 'bozze-upload-heading';
  return (
    <section
      data-testid="bozze-upload-widget"
      aria-labelledby={headingId}
      style={{
        marginBottom: 24,
        padding: '20px 22px',
        background: tokens.surface,
        border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radius.lg,
      }}
    >
      <h2
        id={headingId}
        style={{
          fontFamily: tokens.fonts.serif,
          fontSize: 18,
          fontWeight: 700,
          margin: 0,
          color: tokens.text,
        }}
      >
        Carica un documento per generare una bozza
      </h2>
      <p
        style={{
          color: tokens.textMuted,
          margin: '4px 0 16px 0',
          fontSize: 13,
        }}
      >
        PDF, massimo 10 MB. Selezioni poi un principio da arricchire.
      </p>

      {errorMsg && (
        <div
          role="alert"
          data-testid="bozze-upload-error"
          style={{
            marginBottom: 12,
            color: tokens.wrong,
            fontSize: 13,
          }}
        >
          {errorMsg}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* File input — always visible. Disabled during researching phase. */}
        <div>
          <label
            htmlFor="bozze-upload-file"
            style={{
              fontSize: 12,
              color: tokens.textMuted,
              display: 'block',
              marginBottom: 6,
            }}
          >
            Documento PDF
          </label>
          <input
            id="bozze-upload-file"
            ref={fileInputRef}
            data-testid="bozze-upload-file-input"
            type="file"
            accept=".pdf,application/pdf"
            disabled={phase === 'researching'}
            onChange={handleFileChange}
            style={{
              display: 'block',
              fontSize: 13,
              color: tokens.text,
              minHeight: 44,
            }}
          />
          {uploadedDoc && (
            <div
              data-testid="bozze-upload-uploaded-filename"
              style={{ fontSize: 12, color: tokens.textMuted, marginTop: 6 }}
            >
              Caricato: <strong>{uploadedDoc.filename}</strong>
            </div>
          )}
        </div>

        {/* Phase: uploaded → show pickers. */}
        {phase !== 'idle' && uploadedDoc && (
          <>
            <div>
              <label
                htmlFor="bozze-upload-argomento"
                style={{
                  fontSize: 12,
                  color: tokens.textMuted,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Argomento
              </label>
              <select
                id="bozze-upload-argomento"
                data-testid="bozze-upload-argomento-select"
                value={selectedArgomentoId}
                disabled={phase === 'researching' || !argomenti}
                onChange={(e) => handleArgomentoChange(e.target.value)}
                style={selectStyle(tokens, phase === 'researching' || !argomenti)}
              >
                <option value="">— Selezioni un argomento —</option>
                {argomenti?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name_it}
                  </option>
                ))}
              </select>
              {argomentiError && (
                <div
                  data-testid="bozze-upload-argomenti-error"
                  style={{ fontSize: 12, color: tokens.wrong, marginTop: 4 }}
                >
                  {argomentiError}
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="bozze-upload-principle"
                style={{
                  fontSize: 12,
                  color: tokens.textMuted,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Principio da arricchire
              </label>
              <select
                id="bozze-upload-principle"
                data-testid="bozze-upload-principle-select"
                value={selectedPrincipleId}
                disabled={
                  phase === 'researching' ||
                  !selectedArgomentoId ||
                  principlesLoading ||
                  !principles
                }
                onChange={(e) => setSelectedPrincipleId(e.target.value)}
                style={selectStyle(
                  tokens,
                  phase === 'researching' ||
                    !selectedArgomentoId ||
                    principlesLoading ||
                    !principles,
                )}
              >
                <option value="">
                  {!selectedArgomentoId
                    ? '— Selezioni prima un argomento —'
                    : principlesLoading
                      ? 'Carico i principi…'
                      : '— Selezioni un principio —'}
                </option>
                {principles?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.citation} {p.name_it}
                  </option>
                ))}
              </select>
              {principlesError && (
                <div
                  data-testid="bozze-upload-principles-error"
                  style={{ fontSize: 12, color: tokens.wrong, marginTop: 4 }}
                >
                  {principlesError}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                data-testid="bozze-upload-cancel"
                type="button"
                onClick={resetWidget}
                disabled={phase === 'researching'}
                style={uploadSecondaryButtonStyle(tokens, phase === 'researching')}
              >
                Annulla
              </button>
              <button
                data-testid="bozze-upload-generate"
                type="button"
                onClick={handleGenerate}
                disabled={
                  phase === 'researching' ||
                  !selectedPrincipleId ||
                  !uploadedDoc
                }
                style={uploadPrimaryButtonStyle(
                  tokens,
                  phase === 'researching' || !selectedPrincipleId || !uploadedDoc,
                )}
              >
                {phase === 'researching'
                  ? 'Genero bozza…'
                  : 'Genera bozza dal documento'}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function selectStyle(
  tokens: ReturnType<typeof useTokens>,
  disabled: boolean,
): React.CSSProperties {
  return {
    width: '100%',
    background: tokens.bg,
    color: disabled ? tokens.textMuted : tokens.text,
    border: `1px solid ${tokens.line}`,
    borderRadius: tokens.radius.md,
    padding: '10px 12px',
    fontSize: 14,
    fontFamily: tokens.fonts.body,
    minHeight: 44,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

function uploadPrimaryButtonStyle(
  tokens: ReturnType<typeof useTokens>,
  disabled: boolean,
): React.CSSProperties {
  return {
    background: disabled ? tokens.line : tokens.accent,
    color: disabled ? tokens.textMuted : tokens.mode === 'dark' ? '#1a1510' : '#fff',
    border: 'none',
    borderRadius: tokens.radius.pill,
    padding: '10px 20px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 600,
    minHeight: 44,
  };
}

function uploadSecondaryButtonStyle(
  tokens: ReturnType<typeof useTokens>,
  disabled: boolean,
): React.CSSProperties {
  return {
    background: 'transparent',
    color: disabled ? tokens.textMuted : tokens.text,
    border: `1px solid ${tokens.line}`,
    borderRadius: tokens.radius.pill,
    padding: '10px 20px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    minHeight: 44,
    opacity: disabled ? 0.6 : 1,
  };
}

// ---------------------------------------------------------------------------
// Style helpers + utilities
// ---------------------------------------------------------------------------

function primaryButtonStyle(
  tokens: ReturnType<typeof useTokens>,
  disabled = false,
): React.CSSProperties {
  return {
    background: disabled ? tokens.line : tokens.accent,
    color: disabled ? tokens.textMuted : tokens.mode === 'dark' ? '#1a1510' : '#fff',
    border: 'none',
    borderRadius: tokens.radius.pill,
    padding: '10px 20px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 600,
    minHeight: 44,
  };
}

function secondaryButtonStyle(
  tokens: ReturnType<typeof useTokens>,
): React.CSSProperties {
  return {
    background: 'transparent',
    color: tokens.text,
    border: `1px solid ${tokens.line}`,
    borderRadius: tokens.radius.pill,
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: 13,
    minHeight: 44,
  };
}

/** Muted destructive — neutral background, mirrors the spec note that
 *  Rifiuta should NOT use red. We use the drawerBg + textMuted token. */
function mutedButtonStyle(
  tokens: ReturnType<typeof useTokens>,
): React.CSSProperties {
  return {
    background: tokens.drawerBg,
    color: tokens.textMuted,
    border: `1px solid ${tokens.lineStrong}`,
    borderRadius: tokens.radius.pill,
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: 13,
    minHeight: 44,
  };
}

function pendingPretty(n: number): string {
  if (n <= 0) return 'Nessuna bozza in attesa di revisione.';
  if (n === 1) return '1 bozza in attesa di revisione.';
  return `${n} bozze in attesa di revisione.`;
}

/** Localized relative timestamp, "lei" voice. */
function formatRelativeIt(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const now = Date.now();
  const ms = Math.max(0, now - d.getTime());
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'pochi secondi fa';
  const min = Math.floor(sec / 60);
  if (min === 1) return '1 minuto fa';
  if (min < 60) return `${min} minuti fa`;
  const h = Math.floor(min / 60);
  if (h === 1) return '1 ora fa';
  if (h < 24) return `${h} ore fa`;
  const days = Math.floor(h / 24);
  if (days === 1) return '1 giorno fa';
  if (days < 30) return `${days} giorni fa`;
  // dd/mm/yyyy
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}
